/**
 * Hyperliquid Provider
 *
 * Handles:
 * - Market data (combinedMarkets, popularMarkets) via REST + WebSocket
 * - Error state for rate limiting
 * - Data for sub-providers (marginSummaryById, wallets, portfolioHistory)
 */

import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Sourced directly rather than through the `./types` barrel. The barrel is a
// cycle participant, so reading a *runtime* value through it leaves
// HIP3_DEX_IDS undefined whenever module init enters the cycle from this side.
// Type imports above are erased and stay on the barrel.
import { HIP3_DEX_IDS } from '@infinex-private/perps/core';
import { useReferentiallyStable } from '@infinex/ui-utils/react';

import { aggregatePortfolioHistory } from './aggregation';
import { HYPERLIQUID_CONFIG } from './constants';
import { useHyperliquidRestApi } from './hooks/useHyperliquidRestApi';
import { useMarketDataWebSocket } from './hooks/useMarketDataWebSocket';
import {
  type HyperliquidWalletInfo,
  useWalletFiltering,
} from './hooks/useWalletFiltering';
import { HyperliquidBalancesProvider } from './providers/HyperliquidBalancesProvider';
import { HyperliquidPortfolioHistoryContext } from './providers/HyperliquidPortfolioHistoryProvider';
import type {
  CombinedMarket,
  Hip3DexId,
  Hip3MetaAndAssetCtxsResponse,
  HyperliquidContextValueV2,
  MarginSummary,
  Market,
  Meta,
  PortfolioHistory,
} from './types';

const HyperliquidContext = createContext<HyperliquidContextValueV2 | null>(
  null
);

// Re-export for consumers
export type { HyperliquidWalletInfo };

/** All dex identifiers including main dex (empty string) */
type DexId = '' | Hip3DexId;
const ALL_DEX_IDS: DexId[] = ['', ...HIP3_DEX_IDS];

interface HyperliquidProviderProps {
  children: ReactNode;
  /**
   * Groups frequent WS updates together to prevent WS messages from flooding the app.
   */
  updateThrottleInMs: number;
}

export function HyperliquidProvider({
  children,
  updateThrottleInMs,
}: HyperliquidProviderProps) {
  // Wallet filtering hook (for portfolio history and sub-providers)
  const { eligibleWallets, monitoredWallets } = useWalletFiltering();

  // Market metadata state (fetched via REST API for all dexes)
  const [marketMetadata, setMarketMetadata] = useState<
    Partial<Record<DexId, { meta: Meta | null; assetCtxs: Market[] }>>
  >({});

  // Real-time asset contexts from WebSocket
  const [liveAssetCtxs, setLiveAssetCtxs] = useState<
    Partial<Record<DexId, Market[]>>
  >({});

  // Portfolio history per wallet (fetched via REST)
  const [portfolioHistoryByWallet, setPortfolioHistoryByWallet] = useState<
    Map<string, PortfolioHistory>
  >(new Map());

  // Track which wallets we've already fetched portfolio history for
  const fetchedWalletsRef = useRef<Set<string>>(new Set());

  // Margin summary per wallet (from perps dataset, passed through for backwards compatibility)
  const marginSummaryByIdRef = useRef<Record<string, MarginSummary>>({});

  // Fetch market metadata for all dexes on mount and refresh periodically
  useEffect(() => {
    const fetchAllMarketMetadata = async () => {
      const results = await Promise.allSettled(
        ALL_DEX_IDS.map(async (dex) => {
          const body =
            dex === ''
              ? { type: 'metaAndAssetCtxs' }
              : { type: 'metaAndAssetCtxs', dex };

          const response = await fetch(HYPERLIQUID_CONFIG.API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            console.warn(
              `[Hyperliquid] Failed to fetch ${dex || 'main'} market metadata: ${response.status}`
            );
            return null;
          }

          const [meta, assetCtxs] =
            (await response.json()) as Hip3MetaAndAssetCtxsResponse;
          return { dex, meta, assetCtxs };
        })
      );

      const newMetadata: Partial<
        Record<DexId, { meta: Meta | null; assetCtxs: Market[] }>
      > = {};

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          const { dex, meta, assetCtxs } = result.value;
          newMetadata[dex] = { meta, assetCtxs: assetCtxs || [] };
        }
      });

      if (Object.keys(newMetadata).length > 0) {
        setMarketMetadata((prev) => ({ ...prev, ...newMetadata }));
      }
    };

    // Initial fetch
    void fetchAllMarketMetadata();

    // Refresh every 60 seconds
    const interval = setInterval(fetchAllMarketMetadata, 60000);

    return () => clearInterval(interval);
  }, []);

  // Handle real-time asset context updates from WebSocket
  const handleAssetCtxs = useCallback((dex: string, ctxs: Market[]) => {
    const dexId = (dex || '') as DexId;
    setLiveAssetCtxs((prev) => ({
      ...prev,
      [dexId]: ctxs,
    }));
  }, []);

  // WebSocket for real-time price updates
  const { error: wsError } = useMarketDataWebSocket({
    onAssetCtxs: handleAssetCtxs,
    updateThrottleInMs,
    enabled: true,
  });

  // Memoized callback for portfolio history updates
  const handlePortfolioHistory = useCallback(
    (walletId: string, data: PortfolioHistory) => {
      setPortfolioHistoryByWallet((prev) => {
        const newMap = new Map(prev);
        newMap.set(walletId, data);
        return newMap;
      });
    },
    []
  );

  // REST API hook for portfolio history
  const { fetchInitialPortfolioData } = useHyperliquidRestApi({
    onPortfolioHistory: handlePortfolioHistory,
  });

  // Fetch portfolio history for each monitored wallet (only once per wallet)
  useEffect(() => {
    monitoredWallets.forEach((wallet) => {
      // Skip if we've already fetched for this wallet
      if (fetchedWalletsRef.current.has(wallet.walletId)) {
        return;
      }
      fetchedWalletsRef.current.add(wallet.walletId);
      void fetchInitialPortfolioData(wallet.walletId, wallet.address);
    });
  }, [monitoredWallets, fetchInitialPortfolioData]);

  /*** Derived data ***/

  // Aggregate portfolio history
  const portfolioHistory = useMemo(
    () => aggregatePortfolioHistory(portfolioHistoryByWallet),
    [portfolioHistoryByWallet]
  );

  // Combine market metadata with live WebSocket data
  const combinedMarkets = useMemo(() => {
    const markets: CombinedMarket[] = [];

    ALL_DEX_IDS.forEach((dex) => {
      const metadata = marketMetadata[dex];
      if (!metadata?.meta?.universe) return;

      // Use live data if available, otherwise fall back to REST data
      const assetCtxs = liveAssetCtxs[dex] || metadata.assetCtxs;
      const marketCount = Math.min(
        assetCtxs.length,
        metadata.meta.universe.length
      );

      metadata.meta.universe
        .slice(0, marketCount)
        .forEach((marketMeta, index) => {
          const ctx = assetCtxs[index];
          if (!ctx) return;

          // For main dex, extract coin from name (e.g., "BTC" from "BTC-USD")
          // For HIP-3 dexes, use full name (e.g., "xyz:NVDA")
          const coin =
            dex === ''
              ? marketMeta.name.split('-')[0] || marketMeta.name
              : marketMeta.name;

          const currentPrice = parseFloat(ctx.markPx || '0');
          const previousPrice = parseFloat(ctx.prevDayPx || '0');
          const priceChangePercent =
            previousPrice > 0
              ? ((currentPrice - previousPrice) / previousPrice) * 100
              : 0;

          markets.push({
            ...ctx,
            ...marketMeta,
            coin,
            priceChangePercent,
          });
        });
    });

    return markets;
  }, [marketMetadata, liveAssetCtxs]);

  // Popular markets by volume
  const popularMarkets = useMemo(() => {
    if (combinedMarkets.length === 0) return [];
    return [...combinedMarkets]
      .sort((a, b) => parseFloat(b.dayNtlVlm) - parseFloat(a.dayNtlVlm))
      .slice(0, 6);
  }, [combinedMarkets]);

  // For backwards compatibility with sub-providers
  // These values come from the perps dataset now, but we provide empty defaults
  const marginSummaryById = useReferentiallyStable(
    marginSummaryByIdRef.current
  );
  const wallets = useReferentiallyStable(eligibleWallets);

  // Context value
  const value: HyperliquidContextValueV2 = useMemo(
    () => ({
      error: wsError,
      combinedMarkets,
      popularMarkets,
      portfolioHistory,
      marginSummaryById,
      wallets,
    }),
    [
      wsError,
      combinedMarkets,
      popularMarkets,
      portfolioHistory,
      marginSummaryById,
      wallets,
    ]
  );

  const portfolioHistoryProviderValue = useMemo(
    () => ({ portfolioHistoryByWallet }),
    [portfolioHistoryByWallet]
  );

  return (
    <HyperliquidContext.Provider value={value}>
      <HyperliquidPortfolioHistoryContext.Provider
        value={portfolioHistoryProviderValue}
      >
        <HyperliquidBalancesProvider
          marginSummaryById={marginSummaryById}
          wallets={wallets}
        >
          {children}
        </HyperliquidBalancesProvider>
      </HyperliquidPortfolioHistoryContext.Provider>
    </HyperliquidContext.Provider>
  );
}

/**
 * Hook to access Hyperliquid data (markets, error state)
 * Must be used within HyperliquidProvider
 */
export function useHyperliquid() {
  const context = useContext(HyperliquidContext);
  if (!context) {
    throw new Error('useHyperliquid must be used within HyperliquidProvider');
  }
  return context;
}
